import React, { useEffect } from 'react';
import AppLayout from '../components/AppLayout';

const ManifestoSection = ({ number, title, children }) => (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-16 border-b border-[var(--border-main)] last:border-0">
        <div className="md:col-span-3">
            <span className="font-mono text-xs text-[var(--signal-green)] border border-[var(--signal-green)] px-2 py-1 uppercase tracking-widest">
                {number}
            </span>
        </div>
        <div className="md:col-span-9">
            <h2 className="font-serif text-3xl md:text-4xl text-[var(--fg-primary)] mb-8">{title}</h2>
            <div className="font-serif text-lg text-[var(--fg-secondary)] leading-loose space-y-6">
                {children}
            </div>
        </div>
    </div>
);

const AboutPage = () => {
    useEffect(() => {
        document.title = "AltFinder / Manifesto";
    }, []);

    return (
        <AppLayout>
            <div className="h-full overflow-y-auto bg-[var(--bg-main)]">
                {/* Header */}
                <div className="pt-24 pb-12 px-12 lg:px-24 border-b border-[var(--border-main)]">
                    <p className="font-mono text-xs text-[var(--fg-tertiary)] uppercase tracking-widest mb-6">
                        Established 2024 // Open Protocol Group
                    </p>
                    <h1 className="font-serif text-6xl md:text-8xl text-[var(--fg-primary)] mb-12">
                        Digital <br />
                        Autonomy.
                    </h1>
                    <div className="max-w-2xl font-mono text-sm text-[var(--fg-secondary)] leading-relaxed border-l-2 border-[var(--fg-tertiary)] pl-6">
                        We are building a cartography of the open web. Because the tools you use determine the reality you perceive.
                    </div>
                </div>

                {/* Content Body */}
                <div className="px-12 lg:px-24 max-w-6xl mx-auto">
                    <ManifestoSection number="FIG. 01" title="The Enclosure">
                        <p>
                            The early internet was a bazaar. A chaotic, vibrant exchange of ideas and protocols.
                            Today, it is a shopping mall. Walled gardens owned by five global conglomerates capture
                            value, extract data, and homogenize experience.
                        </p>
                        <p>
                            When you use proprietary software, you are not a user; you are a tenant. And the rent
                            is paid in your cognitive liberty.
                        </p>
                    </ManifestoSection>

                    <ManifestoSection number="FIG. 02" title="The Counter-Map">
                        <p>
                            AltFinder is a weapon of choice. It is a strictly curated index of tools that respect
                            human agency. We catalogue software that is:
                        </p>
                        <ul className="list-none space-y-2 border-l border-[var(--fg-tertiary)] pl-6 py-2 my-4">
                            <li className="text-base font-mono text-[var(--fg-primary)] uppercase tracking-wider">01. Open Source</li>
                            <li className="text-base font-mono text-[var(--fg-primary)] uppercase tracking-wider">02. Privacy Respecting</li>
                            <li className="text-base font-mono text-[var(--fg-primary)] uppercase tracking-wider">03. User Hosted</li>
                        </ul>
                        <p>
                            By choosing these tools, you are not just changing your workflow. You are voting for
                            a different kind of future.
                        </p>
                    </ManifestoSection>

                    <ManifestoSection number="FIG. 03" title="The Protocol">
                        <p>
                            Our index is manually verified. We do not accept payment for placement. We do not
                            track your queries. We operate on a zero-knowledge basis because trust is engineered,
                            not promised.
                        </p>
                    </ManifestoSection>
                </div>

                {/* Footer Signature */}
                <div className="px-12 lg:px-24 py-24 border-t border-[var(--border-main)] bg-[var(--bg-panel)]">
                    <div className="flex flex-col md:flex-row justify-between items-end gap-12">
                        <div>
                            <div className="w-16 h-1 bg-[var(--fg-primary)] mb-8"></div>
                            <p className="font-serif text-2xl text-[var(--fg-primary)] italic">
                                "The best alternative is the one you own."
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-12 text-xs font-mono uppercase tracking-widest text-[var(--fg-tertiary)]">
                            <div>
                                <span className="block text-[var(--fg-primary)] mb-2">Location</span>
                                Decentralized
                            </div>
                            <div>
                                <span className="block text-[var(--fg-primary)] mb-2">Contact</span>
                                Encrypted
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default AboutPage;
